/**
 * DepartmentDetailPage - 部署詳細ページ
 * /company/:slug/departments/:deptId
 *
 * TeamDetailPage と同等の情報を表示:
 * StatCards, レーダーチャート(メンバーオーバーレイ付), スペクトラム,
 * AI分析, チーム一覧, メンバーカード, ギャップ分析
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled, { keyframes, useTheme } from 'styled-components';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import companyService from '../../services/companyService';
import RadarChart from '../../components/company/RadarChart';
import TeamCard from '../../components/company/TeamCard';
import MemberCard from '../../components/company/MemberCard';
import StatCard from '../../components/company/StatCard';
import SpectrumBar from '../../components/company/SpectrumBar';

/* ── Constants ── */
const AXIS_KEYS = ['activation', 'judgment', 'choice', 'resonance', 'awareness'];
const OVERLAY_COLORS = [
  '#EC4899', '#F59E0B', '#10B981', '#3B82F6', '#A855F7',
  '#EF4444', '#14B8A6', '#F97316', '#6366F1', '#84CC16',
];

/* ── Styled Components ── */

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const PageContainer = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 48px 24px 80px;
  animation: ${fadeIn} 0.35s ease-out;
  @media (max-width: ${p => p.theme.breakpoints?.tablet || '768px'}) { padding: 24px 16px 64px; }
`;

const Breadcrumb = styled.nav`
  display: flex; align-items: center; flex-wrap: wrap; gap: 6px;
  margin-bottom: 24px; font-size: 0.82rem; color: ${p => p.theme.colors.textSecondary};
`;

const BreadcrumbLink = styled(Link)`
  color: ${p => p.theme.colors.textSecondary}; text-decoration: none; transition: color 0.2s;
  &:hover { color: ${p => p.theme.colors.primary}; }
`;

const BreadcrumbSep = styled.span`
  color: ${p => p.theme.colors.border}; user-select: none;
`;

const BreadcrumbCurrent = styled.span`
  color: ${p => p.theme.colors.text}; font-weight: 600;
`;

const DeptHeader = styled.div`
  margin-bottom: 32px;
`;

const DeptTitleRow = styled.div`
  display: flex; align-items: center; flex-wrap: wrap; gap: 12px 16px; margin-bottom: 8px;
`;

const DeptName = styled.h1`
  font-size: 1.8rem; font-weight: 800; color: ${p => p.theme.colors.text}; margin: 0;
  line-height: 1.2;
  @media (max-width: ${p => p.theme.breakpoints?.mobile || '640px'}) { font-size: 1.4rem; }
`;

const TypeBadge = styled(Link)`
  display: inline-flex; align-items: center; padding: 4px 14px;
  border-radius: ${p => p.theme.borderRadius?.full || '9999px'};
  background: ${p => p.theme.colors.primaryGlow || 'rgba(99,102,241,0.12)'};
  border: 1px solid ${p => p.theme.colors.primary};
  font-family: ${p => p.theme.fonts?.mono}; font-size: 0.8rem; font-weight: 700;
  color: ${p => p.theme.colors.primary}; text-decoration: none;
  &:hover { opacity: 0.8; }
`;

const MemberCountBadge = styled.span`
  display: inline-flex; align-items: center; gap: 6px; padding: 4px 14px;
  border-radius: ${p => p.theme.borderRadius?.full || '9999px'};
  background: ${p => p.theme.colors.bgTertiary}; border: 1px solid ${p => p.theme.colors.border};
  font-size: 0.8rem; font-weight: 600; color: ${p => p.theme.colors.textSecondary};
`;

const StatsGrid = styled.div`
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 32px;
  @media (max-width: ${p => p.theme.breakpoints?.tablet || '768px'}) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: ${p => p.theme.breakpoints?.mobile || '640px'}) { grid-template-columns: 1fr; }
`;

const VisualizationGrid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 40px;
  @media (max-width: ${p => p.theme.breakpoints?.tablet || '768px'}) { grid-template-columns: 1fr; }
`;

const ChartPanel = styled.div`
  background: ${p => p.theme.colors.bgSecondary}; border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius?.lg || '12px'}; padding: 24px;
`;

const PanelTitle = styled.h2`
  font-size: 0.9rem; font-weight: 600; color: ${p => p.theme.colors.textSecondary};
  margin: 0 0 20px 0; text-transform: uppercase; letter-spacing: 0.5px;
`;

const ChartCenter = styled.div`
  display: flex; align-items: center; justify-content: center;
`;

const SectionHeader = styled.div`
  display: flex; align-items: center; gap: 12px; margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
  font-size: 1.2rem; font-weight: 700; color: ${p => p.theme.colors.text}; margin: 0;
`;

const CountChip = styled.span`
  font-size: 0.78rem; font-family: ${p => p.theme.fonts?.mono}; font-weight: 600;
  color: ${p => p.theme.colors.primary};
  background: ${p => p.theme.colors.primaryGlow || 'rgba(99,102,241,0.12)'};
  padding: 2px 10px; border-radius: ${p => p.theme.borderRadius?.full || '9999px'};
`;

const TeamsGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 16px; margin-bottom: 32px;
`;

const MembersGrid = styled.div`
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 40px;
  @media (max-width: ${p => p.theme.breakpoints?.tablet || '768px'}) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: ${p => p.theme.breakpoints?.mobile || '640px'}) { grid-template-columns: 1fr; }
`;

/* ---- Gap Analysis ---- */
const GapSection = styled.div`
  margin-bottom: 40px;
`;

const GapGrid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 20px;
  @media (max-width: ${p => p.theme.breakpoints?.mobile || '640px'}) { grid-template-columns: 1fr; }
`;

const GapPanel = styled.div`
  background: ${p => p.theme.colors.bgSecondary}; border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius?.lg || '12px'}; padding: 20px;
`;

const GapPanelTitle = styled.h3`
  font-size: 0.85rem; font-weight: 600; color: ${p => p.theme.colors.textSecondary};
  text-transform: uppercase; letter-spacing: 0.5px; margin: 0 0 14px 0;
`;

const TagList = styled.div`
  display: flex; flex-wrap: wrap; gap: 8px;
`;

const GapTag = styled.span`
  display: inline-flex; align-items: center; padding: 5px 12px;
  border-radius: ${p => p.theme.borderRadius?.md || '8px'};
  font-size: 0.82rem; font-weight: 600;
  background: ${p => p.$bg}; color: ${p => p.$color};
  border: 1px solid ${p => p.$borderColor || 'transparent'};
`;

const GapEmpty = styled.p`
  font-size: 0.85rem; color: ${p => p.theme.colors.textSecondary}; margin: 0; font-style: italic;
`;

const EmptyState = styled.div`
  text-align: center; padding: 48px 24px; color: ${p => p.theme.colors.textSecondary};
  background: ${p => p.theme.colors.bgSecondary};
  border: 1px dashed ${p => p.theme.colors.border}; border-radius: ${p => p.theme.borderRadius?.lg || '12px'};
  font-size: 0.9rem; line-height: 1.6;
`;

/* ---- AI Analysis ---- */

const AiSection = styled.div`
  margin: 32px 0;
`;

const AiButton = styled.button`
  display: inline-flex; align-items: center; gap: 8px; padding: 12px 24px;
  border-radius: ${p => p.theme.borderRadius?.md || '8px'}; color: #fff; border: none;
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}, ${p => p.theme.colors.secondary || '#7C3AED'});
  font-weight: 600; font-size: 0.9rem; cursor: pointer; transition: opacity 0.2s, transform 0.15s; margin-bottom: 20px;
  &:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const AiResultGrid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 16px;
  @media (max-width: ${p => p.theme.breakpoints?.tablet || '768px'}) { grid-template-columns: 1fr; }
`;

const AiCard = styled.div`
  background: ${p => p.theme.colors.bgSecondary}; border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius?.lg || '12px'}; padding: 20px;
`;

const AiCardTitle = styled.h3`
  font-size: 0.85rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px;
  margin: 0 0 12px 0; color: ${p => p.$color || p.theme.colors.primary};
`;

const AiList = styled.ul`
  list-style: none; padding: 0; margin: 0;
`;

const AiListItem = styled.li`
  font-size: 0.85rem; line-height: 1.6; color: ${p => p.theme.colors.text};
  padding: 6px 0; border-bottom: 1px solid ${p => p.theme.colors.border}20;
  &:last-child { border-bottom: none; }
`;

const AiSummary = styled.div`
  grid-column: 1 / -1; background: ${p => p.theme.colors.bgSecondary};
  border: 1px solid ${p => p.theme.colors.primary}40; border-radius: ${p => p.theme.borderRadius?.lg || '12px'};
  padding: 20px; font-size: 0.9rem; line-height: 1.7; color: ${p => p.theme.colors.text};
`;

const KeyPersonsGrid = styled.div`
  grid-column: 1 / -1;
  display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 16px;
`;

const KeyPersonCard = styled.div`
  background: ${p => p.theme.colors.bgSecondary}; border: 1px solid ${p => p.theme.colors.primary}30;
  border-radius: ${p => p.theme.borderRadius?.lg || '12px'}; padding: 16px;
  display: flex; flex-direction: column; gap: 8px;
`;

const KeyPersonName = styled.div`
  font-size: 0.9rem; font-weight: 700; color: ${p => p.theme.colors.text};
  display: flex; align-items: center; gap: 6px;
`;

const KeyPersonTendency = styled.div`
  font-size: 0.82rem; line-height: 1.5; color: ${p => p.theme.colors.textSecondary};
`;

const KeyPersonReason = styled.div`
  font-size: 0.82rem; line-height: 1.5; color: ${p => p.theme.colors.text};
  padding-top: 4px; border-top: 1px solid ${p => p.theme.colors.border}30;
`;

const RoleAptitudesSection = styled.div`
  grid-column: 1 / -1;
`;

const RolePersonRow = styled.div`
  display: flex; align-items: flex-start; gap: 12px; padding: 10px 0;
  border-bottom: 1px solid ${p => p.theme.colors.border}20;
  &:last-child { border-bottom: none; }
`;

const RolePersonName = styled.span`
  font-size: 0.85rem; font-weight: 600; color: ${p => p.theme.colors.text};
  min-width: 120px; flex-shrink: 0;
`;

const RoleTags = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px;
`;

const RoleTag = styled.span`
  display: inline-flex; align-items: center; padding: 3px 10px;
  border-radius: ${p => p.theme.borderRadius?.full || '9999px'}; font-size: 0.75rem; font-weight: 600;
  background: ${p => p.theme.colors.primaryGlow || 'rgba(99,102,241,0.12)'}; color: ${p => p.theme.colors.primary};
  cursor: default; position: relative;
  &:hover::after {
    content: attr(title); position: absolute; bottom: calc(100% + 6px); left: 50%;
    transform: translateX(-50%); background: ${p => p.theme.colors.bgPrimary};
    border: 1px solid ${p => p.theme.colors.border}; padding: 6px 10px; border-radius: 6px;
    font-size: 0.75rem; font-weight: 400; color: ${p => p.theme.colors.text};
    white-space: nowrap; max-width: 300px; z-index: 10; pointer-events: none;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  }
`;

const aiPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

const AiLoadingText = styled.span`
  animation: ${aiPulse} 1.5s ease-in-out infinite;
`;

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const SkeletonBlock = styled.div`
  height: ${p => p.$h || '20px'}; width: ${p => p.$w || '100%'}; border-radius: 8px;
  background: linear-gradient(90deg, ${p => p.theme.colors.bgTertiary} 25%, ${p => p.theme.colors.bgSecondary} 50%, ${p => p.theme.colors.bgTertiary} 75%);
  background-size: 800px 100%; animation: ${shimmer} 1.6s infinite; margin-bottom: ${p => p.$mb || '0'};
`;

const ErrorContainer = styled.div`
  text-align: center; padding: 64px 24px;
`;

const ErrorTitle = styled.h2`
  font-size: 1.3rem; font-weight: 700; color: ${p => p.theme.colors.text}; margin: 0 0 12px 0;
`;

const ErrorMessage = styled.p`
  font-size: 0.9rem; color: ${p => p.theme.colors.textSecondary}; margin: 0 0 24px 0;
`;

const RetryButton = styled.button`
  padding: 10px 24px; border-radius: ${p => p.theme.borderRadius?.md || '8px'};
  background: ${p => p.theme.colors.primary}; color: #fff; border: none;
  font-weight: 600; font-size: 0.9rem; cursor: pointer; transition: background 0.2s;
  &:hover { background: ${p => p.theme.colors.secondary}; }
`;

const BackLink = styled(Link)`
  display: inline-flex; align-items: center; gap: 6px;
  color: ${p => p.theme.colors.textSecondary}; text-decoration: none; font-size: 0.85rem;
  margin-top: 16px; transition: color 0.2s;
  &:hover { color: ${p => p.theme.colors.primary}; }
`;

/* ── SVG Icons ── */
const ChevronRightIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
);

const UsersIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

/* ── Component ── */
const DepartmentDetailPage = () => {
  const { slug, deptId } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const { isAuthenticated } = useAuth();
  const theme = useTheme();

  const [company, setCompany] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiCachedAt, setAiCachedAt] = useState(null);

  const axisNames = useMemo(() => ({
    activation: t('行動特性', 'Behavioral'),
    judgment: t('判断基準', 'Decision'),
    choice: t('価値観', 'Values'),
    resonance: t('共感性', 'Empathy'),
    awareness: t('自己認識', 'Self-Awareness'),
  }), [t]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [companyData, analysisData, cachedAi] = await Promise.all([
        companyService.getCompany(slug),
        companyService.getDeptAnalysis(slug, deptId).catch(() => null),
        companyService.getCachedDeptAiAnalysis(slug, deptId).catch(() => null),
      ]);
      setCompany(companyData);
      setAnalysis(analysisData);
      if (cachedAi && cachedAi.cached && cachedAi.result) {
        setAiResult(cachedAi.result);
        setAiCachedAt(cachedAi.created_at);
      }
    } catch (err) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [slug, deptId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const runAiAnalysis = useCallback(async () => {
    setAiLoading(true);
    setAiError(null);
    try {
      const result = await companyService.getDeptAiAnalysis(slug, deptId);
      setAiResult(result);
      setAiCachedAt(new Date().toISOString());
    } catch (err) {
      setAiError(err.message || t('AI分析に失敗しました', 'AI analysis failed'));
    } finally {
      setAiLoading(false);
    }
  }, [slug, deptId, t]);

  // Find the department
  const department = useMemo(() => {
    if (!company?.departments) return null;
    return company.departments.find(d => d.id === deptId);
  }, [company, deptId]);

  // Find teams for this department
  const deptTeams = useMemo(() => {
    if (!department) return [];
    return department.teams || [];
  }, [department]);

  // Find members by department_id (includes members with no team)
  const deptMembers = useMemo(() => {
    if (!company?.members) return [];
    return company.members.filter(m => m.department_id === deptId);
  }, [company, deptId]);

  // Radar chart: main centroid from analysis endpoint
  const radarData = useMemo(() => {
    if (!analysis?.centroid) return [];
    return AXIS_KEYS.map(key => ({
      label: axisNames[key],
      value: Math.min(Math.max(analysis.centroid[key] ?? 0, 0), 1),
    }));
  }, [analysis, axisNames]);

  // Radar chart: member overlays (show only if <10 members)
  const memberOverlays = useMemo(() => {
    if (deptMembers.length === 0 || deptMembers.length >= 10) return [];
    return deptMembers
      .filter(m => m.axes)
      .map((m, idx) => {
        const needsNormalize = Object.values(m.axes).some(v => typeof v === 'number' && v <= 1);
        return {
          data: AXIS_KEYS.map(key => {
            const raw = m.axes[key] ?? 0;
            const norm = needsNormalize ? Math.min(Math.max(raw, 0), 1) : raw / 1000;
            return { label: axisNames[key], value: norm };
          }),
          color: OVERLAY_COLORS[idx % OVERLAY_COLORS.length],
          opacity: 0.15,
        };
      });
  }, [deptMembers, axisNames]);

  // Gap analysis data
  const gapData = useMemo(() => {
    if (!analysis) return null;
    const missing = analysis.missing_categories || analysis.gap?.missing_categories || null;
    const risk = analysis.risk_categories || analysis.gap?.risk_categories || null;
    if (!missing && !risk) return null;
    return { missing: missing || [], risk: risk || [] };
  }, [analysis]);

  /* ── Render states ── */

  if (!isAuthenticated) {
    return (
      <PageContainer>
        <ErrorContainer>
          <ErrorTitle>{t('ログインが必要です', 'Login Required')}</ErrorTitle>
          <ErrorMessage>{t('このページを利用するにはログインまたは新規登録が必要です。', 'Please log in or sign up to access this page.')}</ErrorMessage>
          <RetryButton onClick={() => navigate('/')}>
            {t('ログイン / 新規登録', 'Login / Sign Up')}
          </RetryButton>
        </ErrorContainer>
      </PageContainer>
    );
  }

  if (loading) {
    return (
      <PageContainer>
        <SkeletonBlock $w="200px" $h="16px" $mb="24px" />
        <SkeletonBlock $w="300px" $h="36px" $mb="12px" />
        <SkeletonBlock $w="180px" $h="20px" $mb="32px" />
        <StatsGrid>
          <SkeletonBlock $h="90px" /><SkeletonBlock $h="90px" />
          <SkeletonBlock $h="90px" /><SkeletonBlock $h="90px" />
        </StatsGrid>
        <VisualizationGrid>
          <SkeletonBlock $h="300px" />
          <SkeletonBlock $h="300px" />
        </VisualizationGrid>
      </PageContainer>
    );
  }

  if (error) {
    const isAuthError = error === 'Not authenticated' || error === 'Invalid or expired token';
    return (
      <PageContainer>
        <ErrorContainer>
          <ErrorTitle>{isAuthError ? t('ログインが必要です', 'Login Required') : t('エラーが発生しました', 'An Error Occurred')}</ErrorTitle>
          <ErrorMessage>{isAuthError ? t('このページを利用するにはログインまたは新規登録が必要です。', 'Please log in or sign up to access this page.') : error}</ErrorMessage>
          <RetryButton onClick={isAuthError ? () => navigate('/') : fetchData}>
            {isAuthError ? t('ログイン / 新規登録', 'Login / Sign Up') : t('再試行', 'Retry')}
          </RetryButton>
          {!isAuthError && (
            <>
              <br />
              <BackLink to={`/company/${slug}`}>
                <ArrowLeftIcon />
                {t('ダッシュボードに戻る', 'Back to Dashboard')}
              </BackLink>
            </>
          )}
        </ErrorContainer>
      </PageContainer>
    );
  }

  if (!department) {
    return (
      <PageContainer>
        <ErrorContainer>
          <ErrorTitle>{t('部署が見つかりません', 'Department not found')}</ErrorTitle>
          <BackLink to={`/company/${slug}`}>
            <ArrowLeftIcon />
            {t('ダッシュボードに戻る', 'Back to Dashboard')}
          </BackLink>
        </ErrorContainer>
      </PageContainer>
    );
  }

  /* ── Main render ── */
  const typeCode = analysis?.nearest_type?.type_code || null;
  const memberCount = deptMembers.length;
  const diagnosedCount = deptMembers.filter(m => m.type_code).length;

  const strongestAxis = analysis?.axis_balance?.strongest_axis
    ? axisNames[analysis.axis_balance.strongest_axis] || analysis.axis_balance.strongest_axis
    : '--';

  const weakestAxis = analysis?.axis_balance?.weakest_axis
    ? axisNames[analysis.axis_balance.weakest_axis] || analysis.axis_balance.weakest_axis
    : '--';

  return (
    <PageContainer>
      {/* Breadcrumb */}
      <Breadcrumb>
        <BreadcrumbLink to={`/company/${slug}`}>{company?.name || slug}</BreadcrumbLink>
        <BreadcrumbSep><ChevronRightIcon /></BreadcrumbSep>
        <BreadcrumbCurrent>{department.name}</BreadcrumbCurrent>
      </Breadcrumb>

      {/* Header */}
      <DeptHeader>
        <DeptTitleRow>
          <DeptName>{department.name}</DeptName>
          {typeCode && <TypeBadge to={`/types/${typeCode}`}>{typeCode}</TypeBadge>}
          <MemberCountBadge>
            <UsersIcon />
            {memberCount} {t('名', memberCount === 1 ? 'member' : 'members')}
          </MemberCountBadge>
        </DeptTitleRow>
      </DeptHeader>

      {/* Stats Cards */}
      <StatsGrid>
        <StatCard
          title={t('メンバー', 'Members')}
          value={`${diagnosedCount} / ${memberCount}`}
          subtitle={t('行動特性分析 完了数', 'Behavioral analysis completed')}
        />
        <StatCard
          title={t('部署タイプ', 'Dept Type')}
          value={typeCode || '--'}
          subtitle={t('統計的分析に基づく分類', 'Based on statistical analysis')}
        />
        <StatCard
          title={t('強み', 'Strength')}
          value={strongestAxis}
          subtitle={t('部署の優位特性', 'Dominant dept trait')}
        />
        <StatCard
          title={t('弱み', 'Weakness')}
          value={weakestAxis}
          subtitle={t('成長ポテンシャル', 'Growth potential')}
        />
      </StatsGrid>

      {/* Visualization */}
      <VisualizationGrid>
        <ChartPanel>
          <PanelTitle>{t('部署レーダーチャート', 'Department Radar Chart')}</PanelTitle>
          {radarData.length === 5 ? (
            <ChartCenter>
              <RadarChart data={radarData} size={280} overlays={memberOverlays} />
            </ChartCenter>
          ) : (
            <EmptyState>
              {t(
                '分析データがまだありません。メンバーの行動特性分析が完了すると表示されます。',
                'No analysis data yet. This will be displayed once members complete their behavioral analysis.'
              )}
            </EmptyState>
          )}
          {memberOverlays.length > 0 && (
            <div style={{ marginTop: 12, textAlign: 'center' }}>
              <span style={{ fontSize: '0.72rem', color: theme.colors.textSecondary }}>
                {t(
                  '半透明のラインは各メンバーの個別プロファイルを示しています',
                  'Translucent lines represent individual member profiles'
                )}
              </span>
            </div>
          )}
        </ChartPanel>

        <ChartPanel>
          <PanelTitle>{t('構造スペクトラム', 'Structure Spectrum')}</PanelTitle>
          <SpectrumBar
            axes={analysis?.centroid || {}}
            normalize
            lang={lang}
          />
        </ChartPanel>
      </VisualizationGrid>

      {/* AI Analysis */}
      <AiSection>
        <SectionHeader>
          <SectionTitle>{t('AI部署分析', 'AI Department Analysis')}</SectionTitle>
          {aiCachedAt && (
            <span style={{ fontSize: '0.75rem', color: theme.colors.textSecondary }}>
              {new Date(aiCachedAt).toLocaleDateString()}
            </span>
          )}
        </SectionHeader>

        {!aiResult && !aiLoading && (
          <AiButton onClick={runAiAnalysis} disabled={aiLoading || memberCount < 2}>
            {memberCount < 2
              ? t('メンバー2名以上で分析可能', 'Requires 2+ members')
              : t('AIでこの部署を分析する', 'Analyze this department with AI')}
          </AiButton>
        )}

        {!aiResult && aiLoading && (
          <AiButton disabled>
            <AiLoadingText>{t('AI分析中...', 'Analyzing...')}</AiLoadingText>
          </AiButton>
        )}

        {aiError && (
          <p style={{ color: theme.colors.error || '#EF4444', fontSize: '0.85rem' }}>{aiError}</p>
        )}

        {aiResult && (
          <div style={{ position: 'relative' }}>
            {aiLoading && (
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(0,0,0,0.6)', borderRadius: '12px', zIndex: 10,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px',
              }}>
                <div style={{ width: '32px', height: '32px', border: '3px solid rgba(255,255,255,0.2)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                <span style={{ color: '#fff', fontSize: '0.9rem', fontWeight: 600 }}>
                  {t('再分析中...', 'Re-analyzing...')}
                </span>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            )}
            <AiResultGrid>
              {aiResult.overview && (
                <AiSummary>{aiResult.overview}</AiSummary>
              )}
              {aiResult.structural_strengths?.length > 0 && (
                <AiCard>
                  <AiCardTitle $color="#10B981">{t('構造的な強み', 'Structural Strengths')}</AiCardTitle>
                  <AiList>{aiResult.structural_strengths.map((s, i) => <AiListItem key={i}>{s}</AiListItem>)}</AiList>
                </AiCard>
              )}
              {aiResult.structural_risks?.length > 0 && (
                <AiCard>
                  <AiCardTitle $color="#EF4444">{t('構造的なリスク', 'Structural Risks')}</AiCardTitle>
                  <AiList>{aiResult.structural_risks.map((s, i) => <AiListItem key={i}>{s}</AiListItem>)}</AiList>
                </AiCard>
              )}
              {aiResult.position_fit?.length > 0 && (
                <AiCard>
                  <AiCardTitle $color="#F59E0B">{t('配置適性', 'Position Fit')}</AiCardTitle>
                  <AiList>{aiResult.position_fit.map((s, i) => <AiListItem key={i}>{s}</AiListItem>)}</AiList>
                </AiCard>
              )}
              {aiResult.team_composition_advice?.length > 0 && (
                <AiCard>
                  <AiCardTitle $color="#3B82F6">{t('チーム構成アドバイス', 'Team Composition')}</AiCardTitle>
                  <AiList>{aiResult.team_composition_advice.map((s, i) => <AiListItem key={i}>{s}</AiListItem>)}</AiList>
                </AiCard>
              )}
              {aiResult.missing_capabilities?.length > 0 && (
                <AiCard style={{ gridColumn: '1 / -1' }}>
                  <AiCardTitle $color="#8B5CF6">{t('補強が望まれる人材像', 'Missing Capabilities')}</AiCardTitle>
                  <AiList>{aiResult.missing_capabilities.map((s, i) => <AiListItem key={i}>{s}</AiListItem>)}</AiList>
                </AiCard>
              )}
              {aiResult.action_items?.length > 0 && (
                <AiCard style={{ gridColumn: '1 / -1' }}>
                  <AiCardTitle>{t('アクションの方向性', 'Action Items')}</AiCardTitle>
                  <AiList>{aiResult.action_items.map((s, i) => <AiListItem key={i}>{s}</AiListItem>)}</AiList>
                </AiCard>
              )}

              {aiResult.key_persons?.length > 0 && (
                <>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <AiCardTitle $color="#D946EF">{t('キーパーソン', 'Key Persons')}</AiCardTitle>
                  </div>
                  <KeyPersonsGrid>
                    {aiResult.key_persons.map((person, i) => (
                      <KeyPersonCard key={i}>
                        <KeyPersonName>
                          <span style={{ fontSize: '1rem' }}>&#x1F451;</span>
                          {person.name}
                        </KeyPersonName>
                        <KeyPersonTendency>{person.tendency}</KeyPersonTendency>
                        <KeyPersonReason>{person.reason}</KeyPersonReason>
                      </KeyPersonCard>
                    ))}
                  </KeyPersonsGrid>
                </>
              )}

              {aiResult.role_aptitudes?.length > 0 && (
                <RoleAptitudesSection>
                  <AiCard>
                    <AiCardTitle $color="#0EA5E9">{t('役割適性', 'Role Aptitudes')}</AiCardTitle>
                    {aiResult.role_aptitudes.map((person, i) => (
                      <RolePersonRow key={i}>
                        <RolePersonName>{person.name}</RolePersonName>
                        <RoleTags>
                          {(person.aptitudes || []).map((apt, j) => (
                            <RoleTag key={j} title={apt.reason}>
                              {apt.role}
                            </RoleTag>
                          ))}
                        </RoleTags>
                      </RolePersonRow>
                    ))}
                  </AiCard>
                </RoleAptitudesSection>
              )}
            </AiResultGrid>
            <div style={{ marginTop: '12px', textAlign: 'right' }}>
              <AiButton onClick={runAiAnalysis} disabled={aiLoading} style={{ fontSize: '0.8rem', padding: '8px 16px' }}>
                {t('再分析', 'Re-analyze')}
              </AiButton>
            </div>
          </div>
        )}
      </AiSection>

      {/* Members */}
      <SectionHeader>
        <SectionTitle>{t('メンバー', 'Members')}</SectionTitle>
        <CountChip>{memberCount}</CountChip>
      </SectionHeader>

      {memberCount > 0 ? (
        <MembersGrid>
          {deptMembers.map(member => (
            <MemberCard
              key={member.id}
              member={member}
              onClick={() => navigate(`/company/${slug}/members/${member.id}`)}
            />
          ))}
        </MembersGrid>
      ) : (
        <EmptyState style={{ marginBottom: 40 }}>
          {t(
            'この部署にはまだメンバーがいません。ダッシュボードからメンバーを部署に割り当ててください。',
            'No members in this department yet. Assign members to this department from the dashboard.'
          )}
        </EmptyState>
      )}

      {/* Gap Analysis */}
      {gapData && (
        <GapSection>
          <SectionHeader>
            <SectionTitle>{t('ギャップ分析', 'Gap Analysis')}</SectionTitle>
          </SectionHeader>

          <GapGrid>
            <GapPanel>
              <GapPanelTitle>{t('不足カテゴリ', 'Missing Categories')}</GapPanelTitle>
              {gapData.missing.length > 0 ? (
                <TagList>
                  {gapData.missing.map(cat => (
                    <GapTag
                      key={cat}
                      $bg={`${theme.colors.accent || '#EC4899'}15`}
                      $color={theme.colors.accent || '#EC4899'}
                      $borderColor={`${theme.colors.accent || '#EC4899'}40`}
                    >
                      {cat}
                    </GapTag>
                  ))}
                </TagList>
              ) : (
                <GapEmpty>{t('不足しているカテゴリはありません。', 'No missing categories detected.')}</GapEmpty>
              )}
            </GapPanel>

            <GapPanel>
              <GapPanelTitle>{t('リスクカテゴリ (1名のみ)', 'Risk Categories (Single Member)')}</GapPanelTitle>
              {gapData.risk.length > 0 ? (
                <TagList>
                  {gapData.risk.map(cat => (
                    <GapTag
                      key={cat}
                      $bg={`${theme.colors.warning || '#F59E0B'}15`}
                      $color={theme.colors.warning || '#F59E0B'}
                      $borderColor={`${theme.colors.warning || '#F59E0B'}40`}
                    >
                      {cat}
                    </GapTag>
                  ))}
                </TagList>
              ) : (
                <GapEmpty>{t('リスクカテゴリはありません。', 'No risk categories detected.')}</GapEmpty>
              )}
            </GapPanel>
          </GapGrid>
        </GapSection>
      )}

      {/* Teams */}
      {deptTeams.length > 0 && (
        <>
          <SectionHeader>
            <SectionTitle>{t('チーム', 'Teams')}</SectionTitle>
            <CountChip>{deptTeams.length}</CountChip>
          </SectionHeader>
          <TeamsGrid>
            {deptTeams.map(team => {
              const teamMembers = deptMembers.filter(m => m.team_id === team.id);
              return (
                <TeamCard
                  key={team.id}
                  team={{ ...team, member_count: teamMembers.length, members: teamMembers }}
                  onClick={() => navigate(`/company/${slug}/teams/${team.id}`)}
                />
              );
            })}
          </TeamsGrid>
        </>
      )}

      {/* Back link */}
      <BackLink to={`/company/${slug}`}>
        <ArrowLeftIcon />
        {t('ダッシュボードに戻る', 'Back to Dashboard')}
      </BackLink>
    </PageContainer>
  );
};

export default DepartmentDetailPage;
